/* eslint-disable indent */
/* eslint-disable eqeqeq */
import {ROOM_CAPACITIES, ROOM_CAPACITY_UNIT, ROOM_MAX_COUNT, ROOM_MIN_COUNT, StateActions, Steps} from 'extensions/multi-system/data/constants';
import {eventStateChange} from 'extensions/multi-system/utils/events';
import {defineCustomElement} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {map} from 'lit/directives/map.js';
import {range} from 'lit/directives/range.js';
import {MasterMatrixController} from './master-matrix-controller';
import stylesheet from './x-step-room-creation.scss?as=stylesheet';

export class StepRoomCreation extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			masterMatrix: {
				attribute: 'master-matrix',
				type: Array
			},
			roomCount: {
				attribute: 'room-count',
				type: Number
			},
			rooms: {
				type: Array
			}
		};
	}

	get allRoomsValid () {
		return this.rooms?.every(room => room.selection);
	}

	constructor () {
		super();

		/**
		 * @type {import('extensions/multi-system/types').MasterMatrix}
		 */
		this.masterMatrix = [];

		/**
		 * @type {string}
		 */
		this.roomCount = 0;

		/**
		 * @type {import('extensions/multi-system/types').Room[]}
		 */
		this.rooms = [];
	}

	connectedCallback () {
		super.connectedCallback();

		/**
		 * @type {MasterMatrixController}
		 */
		this.masterMatrixController = new MasterMatrixController(this.masterMatrix, this);
	}

	handleRoomCountChange (e, roomCount) {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.RoomCountUpdate,
					merge: true,
					payload: roomCount
				},
				{
					action: StateActions.RoomsUpdate,
					merge: true,
					payload: Array.from(range(0, roomCount))
						?.map(n => ({
							capacityClass: void 0,
							name: `Room ${n + 1}`,
							product: void 0,
							selection: void 0
						}))
				},
				{
					action: StateActions.SelectedRoomUpdate,
					merge: true,
					payload: 0
				},
				{
					action: StateActions.OutdoorProductUpdate,
					merge: true,
					payload: void 0
				},
				{
					action: StateActions.UpsellProductsUpdate,
					merge: true,
					payload: void 0
				}
			]
		}));
	}

	handleRoomChange (e) {
		if (!e?.detail) {
			return;
		}

		const {index, name} = e.detail;

		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.RoomNameUpdate,
				merge: true,
				payload: {
					index,
					name
				}
			}
		}));
	}

	/**
	 * @param {Event} e
	 * @param {number} index
	 * @param {import('extensions/multi-system/types').RoomCapacity} selection
	 */
	handleRoomSelection (e, index, selection) {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.RoomSelectionUpdate,
					merge: true,
					payload: {
						index,
						selection
					}
				},
				{
					action: StateActions.RoomProductUpdate,
					merge: true,
					payload: {
						capacityClass: void 0,
						index,
						product: void 0
					}
				},
				// Clear rooms following the currently selected index
				{
					action: StateActions.RoomsUpdate,
					merge: true,
					payload: this.rooms
						?.map((room, _index) => {
							return _index <= index
								? room
								: {
									capacityClass: void 0,
									name: room.name,
									product: void 0,
									selection: void 0
								};
						})
				}
			]
		}));
	}

	handleNextStep () {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.StepUpdate,
					merge: true,
					payload: Steps.Indoor
				}
			]
		}));
	}

	/**
	 * @param {import('extensions/multi-system/types').Room} room
	 * @param {number} i
	 */
	renderRoom (room, i) {
		const availableCapacities = this.masterMatrixController.getAvailableCapacityClasses(this.rooms?.slice(0, i).map(_room => _room.selection?.class) || [], i, this.roomCount);

		return html`
			<x-room @change="${this.handleRoomChange}" index="${i}" name="${room.name}">
				<div class="group center">
					${ROOM_CAPACITIES.filter(capacity => !(this.roomCount >= 5 && capacity.display === '6.00')).map(capacity => {
						const _disabled = !capacity?.class?.some(_c => availableCapacities?.includes(_c));
						const _classes = {
							'is--disabled': _disabled,
							radio: true
						};

						return html`
							<div class="${classMap(_classes)}">
								<input id="rc-${i}-${capacity.display}" name="room${i}-capacity" aria-hidden="true" @change="${e => this.handleRoomSelection(e, i, capacity)}" type="radio" value="${capacity.display}" .checked="${capacity.display == room.selection?.display}" ?disabled="${_disabled}" />
								<label tabindex="0" class="label" for="rc-${i}-${capacity.display}">${capacity.display}${ROOM_CAPACITY_UNIT}</label>
							</div>`;
					})}
				</div>
			</x-room>
		`;
	}

	renderRooms () {
		if (this.roomCount > 0) {
			const gridClasses = {
				grid: true,
				[`count-${this.roomCount}`]: true
			};

			return html`
				<x-section heading="Indoor Cooling Capacity Requirements Per Room">
					<div class="${classMap(gridClasses)}">${this.rooms?.map((room, i) => this.renderRoom(room, i))}</div>
				</x-section>
			`;
		}

		return nothing;
	}

	render () {
		return html`
			<header class="header">
				<h1 class="title">Build a Multi System</h1>
			</header>

			<x-section heading="How Many Rooms?">
				<form class="group">
					${map(range(ROOM_MIN_COUNT, ROOM_MAX_COUNT + 1), n => html`
						<div class="radio">
							<input id="rc-${n}" name="room-count" aria-hidden="true" @change="${e => this.handleRoomCountChange(e, n)}" type="radio" value="${n}" .checked="${n === this.roomCount}" />
							<label tabindex="0" class="label" for="rc-${n}">${n}</label>
						</div>
					`)}
				</form>
			</x-section>

			${this.renderRooms()}

			<footer class="footer">
				<x-tooltip ?disabled="${this.allRoomsValid}" placement="bottom">
					<div slot="content">
						${this.roomCount ? html`Select a capacity for each room to continue` : html`Select how many rooms you need to continue`}
					</div>
					<button class="primary-button" ?disabled="${!this.allRoomsValid}" @click="${this.handleNextStep}">Continue to Products</button>
				</x-tooltip>
			</footer>
		`;
	}
};

defineCustomElement('x-step-room-creation', StepRoomCreation);
